import useI18n from '@hooks/i18n/useI18n';
import { isValidDate } from '@monofront/business/utils';
import {
  Input,
  Label,
  TextInput,
} from '@monofront/design-system/components/FormV2';
import { useTranslations } from 'next-intl';
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

interface IEditBirthDateProps {
  onFormCompleted: () => void;
  confirmAgeUpdate: (dateOfBirth: string) => void;
}

const EditBirthDate = ({
  onFormCompleted,
  confirmAgeUpdate,
}: IEditBirthDateProps) => {
  const [birthDateInput, setBirthDateInput] = useState('');
  const [cursorPosition, setCursorPosition] = useState<number | null>(null);
  const { handleSubmit } = useForm<{ birthDate: string }>();
  const { isGermanMarket } = useI18n();
  const inputRef = useRef<HTMLInputElement>(null);

  const tGlobal = useTranslations('Global');
  const tAccount = useTranslations('Account');

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value.replace(/\D/g, ''); // Remove non-numeric characters
    const separator = isGermanMarket ? '.' : '/';
    let formattedInput = input;

    // Add separators directly after the second and fourth characters
    if (input.length >= 2) {
      formattedInput = `${input.slice(0, 2)}${separator}${input.slice(2)}`;
    }
    if (input.length >= 4) {
      formattedInput = `${formattedInput.slice(
        0,
        5,
      )}${separator}${formattedInput.slice(5)}`;
    }

    setBirthDateInput(formattedInput);

    const currentCursorPosition =
      e.target.selectionStart ?? formattedInput.length;

    // Adjust cursor to skip separators when moving forward or backward
    if (currentCursorPosition === 2 || currentCursorPosition === 5) {
      setCursorPosition(currentCursorPosition + 1); // Move cursor past separator
    } else if (currentCursorPosition === 3 || currentCursorPosition === 6) {
      setCursorPosition(currentCursorPosition - 1); // Allow backspacing before the separator
    } else {
      setCursorPosition(currentCursorPosition);
    }
  };

  // Use useEffect to adjust cursor after input value has been set
  useEffect(() => {
    if (inputRef.current && cursorPosition !== null) {
      inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
    }
  }, [birthDateInput, cursorPosition]);

  const handleBlur = () => {
    if (!isValidDate(birthDateInput)) {
      setBirthDateInput('');
    }
  };

  const handleCancel = () => {
    setBirthDateInput('');
    onFormCompleted();
  };

  const handleConfirm = () => {
    confirmAgeUpdate(birthDateInput);
    setBirthDateInput('');
    onFormCompleted();
  };

  return (
    <form
      onSubmit={handleSubmit(handleConfirm)}
      className="tw-flex tw-flex-col tw-items-center tw-text-center"
    >
      <div className="tw-w-[250px]">
        <TextInput id="register-birth-date">
          <Label>{tAccount('birthDate')}</Label>
          <Input
            ref={inputRef}
            maxLength={10}
            type="text"
            name="birthDate"
            value={birthDateInput}
            onChange={handleInputChange}
            onBlur={handleBlur}
            placeholder={tAccount('birthDateModal.birthDatePlaceholder')}
          />
        </TextInput>
      </div>
      <div className="tw-mt-8 tw-flex tw-gap-x-2">
        <button
          type="button"
          onClick={handleCancel}
          className="tw-lf-button tw-lf-button-secondary tw-lf-button-md"
        >
          {tGlobal('cancel')}
        </button>
        <button
          type="submit"
          className="tw-lf-button tw-lf-button-primary tw-lf-button-md"
        >
          {tGlobal('confirm')}
        </button>
      </div>
    </form>
  );
};

export default EditBirthDate;
